import ReactDOMServer from 'react-dom/server';
import {
    CustomMessageServerImp,
    IM_CUSTOM_MESSAGE,
    IM_CUSTOM_MESSAGE_SEND
} from '@/typings/global';
import { encodeCustomMessageEmoji } from '@/utils/customMessageEmoji';

/** 自定义消息协议管理。 */
class CustomMessageServer implements CustomMessageServerImp {
    private _isPositiveInteger = (value: number): boolean => {
        return typeof value === 'number' && value % 1 === 0 && value > 0;
    };

    private _escapeHtml = (value: unknown): string => {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    };

    private _renderStaticHtml = (message: IM_CUSTOM_MESSAGE): string => {
        try {
            const view = IM.SDKServer.renderCustomMessage(message);
            if (!view) {
                return `<div class="im-message-content">${this._escapeHtml(
                    message.fallbackText
                )}</div>`;
            }
            if (view.kind === 'html') {
                if (typeof view.content !== 'string') {
                    throw new Error('HTML 业务消息视图 content 必须是字符串');
                }
                return view.content;
            }
            if (view.kind !== 'react' || !window.react?.isValidElement?.(view.content)) {
                throw new Error('React 业务消息视图 content 必须是 React Element');
            }
            return ReactDOMServer.renderToStaticMarkup(view.content as React.ReactElement);
        } catch (error) {
            console.error(`业务消息静态渲染失败：${message.type}@${message.version}`, error);
            return `<div class="im-message-content">${this._escapeHtml(
                message.fallbackText
            )}</div>`;
        }
    };

    private _messageToHtml = (
        message: IM_CUSTOM_MESSAGE_SEND
    ): {
        html: string;
        custom: IM_CUSTOM_MESSAGE;
    } => {
        if (!message?.type || !message.version) {
            throw new Error('业务消息 type 或 version 不完整');
        }
        if (!/^[a-zA-Z0-9._-]+$/.test(String(message.type))) {
            throw new Error('业务消息 type 不合法');
        }
        if (!this._isPositiveInteger(message.version)) {
            throw new Error('业务消息 version 必须是正整数');
        }
        const { platform } = IM.SDKServer.getState();
        if (!platform) {
            throw new Error('客服平台 platform 不完整');
        }
        const custom: IM_CUSTOM_MESSAGE = {
            platform,
            type: message.type,
            version: message.version,
            payload: message.payload == null ? {} : message.payload,
            fallbackText: message.fallbackText || ''
        };
        let persistedCustom: IM_CUSTOM_MESSAGE;
        try {
            const serialized = JSON.stringify(custom, (_key, value) => {
                if (
                    typeof value === 'function' ||
                    typeof value === 'symbol' ||
                    typeof value === 'undefined'
                ) {
                    throw new Error('业务消息 payload 包含不可序列化字段');
                }
                return value;
            });
            persistedCustom = JSON.parse(serialized);
        } catch (error) {
            throw new Error('业务消息 payload 必须是可序列化数据');
        }
        return {
            html: this._renderStaticHtml(persistedCustom),
            custom: persistedCustom
        };
    };

    send = async (message: IM_CUSTOM_MESSAGE_SEND): Promise<void> => {
        const { html, custom } = this._messageToHtml(message);
        await IM.IMServer.send(html, undefined, undefined, undefined, {
            custom
        });
    };
}

export default CustomMessageServer;
